import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { liveQuizApi, buildStudentWsUrl } from '../lib/api/liveQuiz';
import { useQuizWebSocket } from '../hooks/useQuizWebSocket';
import '../styles/competition-mode.css';

const OPTIONS = ['A', 'B', 'C', 'D'];
const COLORS  = ['cm-opt-a', 'cm-opt-b', 'cm-opt-c', 'cm-opt-d'];
const SHAPES  = ['▲', '◆', '●', '■'];

// Phases: enter_name → waiting → question → answered → results → leaderboard → abandoned
export default function QuizJoinPage() {
    const { sessionId } = useParams();

    const [phase, setPhase]               = useState('enter_name');
    const [displayName, setDisplayName]   = useState('');
    const [participantId, setParticipantId] = useState(null);
    const [totalQuestions, setTotalQuestions] = useState(0);
    const [currentQIndex, setCurrentQIndex]   = useState(-1);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [hasAnswered, setHasAnswered]       = useState(false);
    const [result, setResult]                 = useState(null);
    const [leaderboard, setLeaderboard]       = useState([]);
    const [error, setError]                   = useState('');
    const [joining, setJoining]               = useState(false);

    const wsUrl = participantId ? buildStudentWsUrl(sessionId, participantId) : null;
    const timeupTimerRef = useRef(null);

    const handleWsMessage = useCallback((msg) => {
        switch (msg.type) {
            case 'session_state':
                setTotalQuestions(msg.total_questions || 0);
                if (msg.status === 'active') {
                    setCurrentQIndex(msg.current_question_index);
                    setPhase(msg.show_results ? 'results' : 'question');
                    if (!msg.show_results) { setHasAnswered(false); setSelectedAnswer(null); }
                } else if (msg.status === 'completed') {
                    setPhase('leaderboard');
                } else {
                    setPhase('waiting');
                }
                break;
            case 'question_start':
                if (timeupTimerRef.current) { clearTimeout(timeupTimerRef.current); timeupTimerRef.current = null; }
                setCurrentQIndex(msg.question_index);
                setTotalQuestions(msg.total_questions);
                setPhase('question');
                setHasAnswered(false);
                setSelectedAnswer(null);
                setResult(null);
                break;
            case 'answer_ack':
                setHasAnswered(true);
                setPhase('answered');
                break;
            case 'show_results':
                if (timeupTimerRef.current) clearTimeout(timeupTimerRef.current);
                setResult(msg);
                setPhase('timeup');
                timeupTimerRef.current = setTimeout(() => { timeupTimerRef.current = null; setPhase('results'); }, 1500);
                break;
            case 'quiz_end':
                if (timeupTimerRef.current) { clearTimeout(timeupTimerRef.current); timeupTimerRef.current = null; }
                setLeaderboard(msg.leaderboard || []);
                setPhase('leaderboard');
                break;
            case 'session_abandoned':
                setPhase('abandoned');
                break;
            default:
                break;
        }
    }, []);

    useQuizWebSocket(wsUrl, handleWsMessage, !!participantId);

    // Fallback Polling Loop to sync quiz state when WebSockets are blocked/unavailable (e.g. AWS App Runner)
    useEffect(() => {
        if (!sessionId || !participantId || phase === 'enter_name') return;

        const interval = setInterval(async () => {
            try {
                const res = await liveQuizApi.pollSession(sessionId, participantId);
                const data = res.data;

                setTotalQuestions(data.total_questions || 0);

                if (data.status === 'waiting') {
                    setPhase('waiting');
                } else if (data.status === 'active') {
                    const servIdx = Number(data.current_question_index);
                    
                    if (data.show_results) {
                        if (data.student_status) {
                            setResult(data.student_status);
                        }
                        setPhase('results');
                    } else {
                        // Reset local answered/option state when moving to a new question
                        if (currentQIndex !== servIdx) {
                            setCurrentQIndex(servIdx);
                            setHasAnswered(false);
                            setSelectedAnswer(null);
                            setResult(null);
                        }
                        setPhase('question');
                    }

                    // Sync answer state if we've already answered this question on the server
                    if (data.student_status && data.student_status.your_answer) {
                        setHasAnswered(true);
                        setSelectedAnswer(data.student_status.your_answer);
                        if (!data.show_results) {
                            setPhase('answered');
                        }
                    }
                } else if (data.status === 'completed') {
                    setLeaderboard(data.leaderboard || []);
                    setPhase('leaderboard');
                } else if (data.status === 'abandoned') {
                    setPhase('abandoned');
                }
            } catch (err) {
                console.error("Error polling student state:", err);
            }
        }, 2000);

        return () => clearInterval(interval);
    }, [sessionId, participantId, phase, currentQIndex]);

    const handleJoin = async (e) => {
        e.preventDefault();
        if (!displayName.trim()) return;
        setJoining(true);
        setError('');
        try {
            const res  = await liveQuizApi.joinSession(sessionId, displayName.trim());
            const data = res.data;
            setParticipantId(data.participant_id);
            setTotalQuestions(data.total_questions);
            setPhase(data.session_status === 'active' ? 'question' : 'waiting');
        } catch (err) {
            setError(err.message || 'Could not join session');
        } finally {
            setJoining(false);
        }
    };

    const handleAnswer = async (option) => {
        if (hasAnswered) return;
        setSelectedAnswer(option);
        try {
            await liveQuizApi.submitAnswer(sessionId, participantId, currentQIndex, option);
            // REST succeeded — mark submitted immediately; WS ack may not arrive if disconnected
            setHasAnswered(true);
            setPhase('answered');
        } catch {
            // If REST also fails, WS ack is the fallback when connection restores
        }
    };

    // ── Render ──────────────────────────────────────────────────────────────

    if (phase === 'enter_name') {
        return (
            <div className="cm-student-root">
                <div className="cm-join-card">
                    <div className="cm-logo">TechGenie</div>
                    <h1>Join Quiz</h1>
                    <p className="cm-sub">Enter your name to join the live quiz</p>
                    <form onSubmit={handleJoin} className="cm-join-form">
                        <input
                            className="cm-name-input"
                            type="text"
                            placeholder="Your display name"
                            value={displayName}
                            onChange={e => setDisplayName(e.target.value)}
                            maxLength={100}
                            autoFocus
                        />
                        {error && <p className="cm-error">{error}</p>}
                        <button
                            className="cm-btn-primary"
                            type="submit"
                            disabled={joining || !displayName.trim()}
                        >
                            {joining ? 'Joining...' : 'Join Quiz →'}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    if (phase === 'waiting') {
        return (
            <div className="cm-student-root">
                <div className="cm-waiting-card">
                    <div className="cm-pulse-ring" />
                    <h2>You're in! 🎉</h2>
                    <p className="cm-name-tag">{displayName}</p>
                    <p className="cm-sub">Waiting for the instructor to start...</p>
                    <p className="cm-sub" style={{ fontSize: '0.82rem', marginTop: '1rem' }}>
                        Look at the screen for questions
                    </p>
                </div>
            </div>
        );
    }

    if (phase === 'question' || phase === 'answered') {
        return (
            <div className="cm-student-question-root">
                {/* Header bar */}
                <div className="cm-question-header">
                    <span className="cm-q-counter">Q {currentQIndex + 1}</span>
                    <span className="cm-q-total-s">/ {totalQuestions}</span>
                </div>

                {/* 2×2 colored shape buttons — fills remaining height */}
                <div className="cm-options-grid">
                    {OPTIONS.map((opt, i) => (
                        <button
                            key={opt}
                            className={[
                                'cm-option-btn',
                                COLORS[i],
                                selectedAnswer === opt ? 'cm-selected' : '',
                                hasAnswered && selectedAnswer !== opt ? 'cm-dimmed' : '',
                            ].join(' ')}
                            onClick={() => handleAnswer(opt)}
                            disabled={hasAnswered}
                        >
                            <span className="cm-opt-shape">{SHAPES[i]}</span>
                            <span className="cm-opt-letter">{opt}</span>
                        </button>
                    ))}
                </div>

                {/* Answered footer */}
                {hasAnswered && (
                    <div className="cm-answered-bar">
                        ✓ Answer submitted — waiting for results...
                    </div>
                )}
            </div>
        );
    }

    if (phase === 'timeup') {
        return (
            <div className="cm-student-root" style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
                <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    justifyContent: 'center', gap: '16px', textAlign: 'center',
                    padding: '40px',
                }}>
                    <div style={{ fontSize: '5rem', animation: 'cm-pulse 0.5s ease infinite' }}>⏰</div>
                    <h2 style={{ color: '#fff', fontSize: '2.5rem', fontWeight: 900, margin: 0 }}>
                        Time's Up!
                    </h2>
                    <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '1rem', margin: 0 }}>
                        Revealing the answer…
                    </p>
                </div>
            </div>
        );
    }

    if (phase === 'results' && result) {
        return (
            <div className="cm-student-root">
                <div className={`cm-result-card ${result.is_correct ? 'cm-correct' : 'cm-wrong'}`}>
                    <div className="cm-result-icon">{result.is_correct ? '✅' : '❌'}</div>
                    <h2>{result.is_correct ? 'Correct!' : 'Incorrect'}</h2>
                    <p className="cm-sub">
                        Correct answer: <strong>{result.correct_option}</strong>
                        {result.your_answer && result.your_answer !== result.correct_option && (
                            <> · Your answer: <strong>{result.your_answer}</strong></>
                        )}
                    </p>
                    <div className="cm-score-badge">Score: {result.your_score} pts</div>
                    <p className="cm-sub" style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
                        Waiting for next question...
                    </p>
                </div>
            </div>
        );
    }

    if (phase === 'leaderboard') {
        return (
            <div className="cm-student-root">
                <div className="cm-lb-card">
                    <h1>Quiz Over! 🏆</h1>
                    <div className="cm-lb-list">
                        {leaderboard.slice(0, 10).map((entry) => (
                            <div
                                key={entry.rank}
                                className={`cm-lb-row ${entry.display_name === displayName ? 'cm-lb-me' : ''}`}
                            >
                                <span className="cm-lb-rank">#{entry.rank}</span>
                                <span className="cm-lb-name">{entry.display_name}</span>
                                <span className="cm-lb-score">{entry.score} pts</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (phase === 'abandoned') {
        return (
            <div className="cm-student-root">
                <div className="cm-join-card">
                    <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🏁</div>
                    <h2>Session Ended</h2>
                    <p className="cm-sub">The instructor has ended this quiz session.</p>
                </div>
            </div>
        );
    }

    return null;
}
